import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import ClientNav from '@/components/ClientNav';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { UserPlus, Mail, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminClients = () => {
  const { token } = useAuth();
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [formData, setFormData] = useState({ email: '', plan_id: 'starter' });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/admin/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data.clients);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/invite-client`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Client invited successfully!');
      setOpen(false);
      setFormData({ email: '', plan_id: 'basic' });
      fetchClients();
    } catch (error) {
      console.error('Failed to invite client:', error);
      toast.error(error.response?.data?.detail || 'Failed to invite client');
    } finally {
      setLoading(false);
    }
  };

  const getPlanBadgeColor = (planId) => {
    switch (planId) {
      case 'professional':
      case 'pro': return 'bg-purple-500/10 text-purple-400';
      case 'business':
      case 'custom': return 'bg-pink-500/10 text-pink-400';
      case 'starter':
      case 'basic':
      default: return 'bg-blue-500/10 text-blue-400';
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile || !selectedClient) {
      toast.error('Please select a client and file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('client_id', selectedClient);

      await axios.post(`${API}/admin/upload-file?client_id=${selectedClient}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('File uploaded successfully!');
      setUploadOpen(false);
      setSelectedFile(null);
      setSelectedClient('');
    } catch (error) {
      console.error('Failed to upload file:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030712]" data-testid="admin-clients-page">
      <ClientNav />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold font-['Syne'] text-white mb-2" data-testid="clients-title">Clients</h1>
            <p className="text-slate-400">Manage your client base</p>
          </div>
          <div className="flex space-x-3">
            <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-500 hover:bg-indigo-600 text-white" data-testid="upload-file-btn">
                  <Upload size={18} className="mr-2" />
                  Upload File
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-950 border-slate-800 text-white">
                <DialogHeader>
                  <DialogTitle>Upload File for Client</DialogTitle>
                  <DialogDescription className="text-slate-400">
                    Upload a file to deliver to your client
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleFileUpload} className="space-y-4 py-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Select Client</label>
                    <Select value={selectedClient} onValueChange={setSelectedClient}>
                      <SelectTrigger className="bg-slate-900 border-slate-800" data-testid="client-select">
                        <SelectValue placeholder="Choose a client" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800 text-white">
                        {clients.map(client => (
                          <SelectItem key={client.id} value={client.id}>{client.email}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">File</label>
                    <Input
                      type="file"
                      onChange={(e) => setSelectedFile(e.target.files[0])}
                      className="bg-slate-900 border-slate-800 text-white"
                      data-testid="file-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={uploading}
                    className="w-full bg-indigo-500 hover:bg-indigo-600 text-white"
                    data-testid="submit-upload"
                  >
                    {uploading ? 'Uploading...' : 'Upload File'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-white text-black hover:bg-slate-200" data-testid="invite-client-btn">
                  <UserPlus size={18} className="mr-2" />
                  Invite Client
                </Button>
              </DialogTrigger>
            <DialogContent className="bg-slate-950 border-slate-800 text-white">
              <DialogHeader>
                <DialogTitle>Invite New Client</DialogTitle>
                <DialogDescription className="text-slate-400">
                  Send an invitation to a new client
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleInvite} className="space-y-4 py-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Email Address</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="client@example.com"
                    required
                    className="bg-slate-900 border-slate-800 text-white"
                    data-testid="client-email-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Plan</label>
                  <Select value={formData.plan_id} onValueChange={(value) => setFormData({...formData, plan_id: value})}>
                    <SelectTrigger className="bg-slate-900 border-slate-800" data-testid="plan-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800 text-white">
                      <SelectItem value="starter">Starter - £25/mo (first month free)</SelectItem>
                      <SelectItem value="professional">Professional - £49/mo</SelectItem>
                      <SelectItem value="business">Business - £89/mo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-indigo-500 hover:bg-indigo-600 text-white"
                  data-testid="submit-invite"
                >
                  {loading ? 'Sending...' : 'Send Invitation'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {clients.length === 0 ? (
          <div className="bg-slate-950 border border-slate-800 rounded-xl p-12 text-center" data-testid="no-clients">
            <Mail className="w-16 h-16 text-slate-700 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No clients yet</h3>
            <p className="text-slate-400">Invite your first client to get started</p>
          </div>
        ) : (
          <div className="bg-slate-950 border border-slate-800 rounded-xl overflow-hidden" data-testid="clients-table">
            <table className="w-full">
              <thead className="bg-slate-900">
                <tr>
                  <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">Email</th>
                  <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">Plan</th>
                  <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">Status</th>
                  <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">Invited</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((client, index) => (
                  <tr key={client.id} className="border-t border-slate-800" data-testid={`client-row-${index}`}>
                    <td className="py-4 px-6 text-white">{client.email}</td>
                    <td className="py-4 px-6">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPlanBadgeColor(client.plan_id)}`}>
                        {client.plan_id || 'basic'}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        client.status === 'active' ? 'bg-green-500/10 text-green-400' : 'bg-gray-500/10 text-gray-400'
                      }`}>
                        {client.status}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-slate-400 text-sm">
                      {format(new Date(client.invited_at), 'MMM dd, yyyy')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default AdminClients;
